/**
 * Portal2 session: reads env-scoped `jwtAuth_{env}` and `projectsInfo_{env}` cookies
 * set by the main portal (same env only).
 */

import { canApplyPortalSession, isPortalHandoff } from '../auth/schedulerSession';
import { getCookie, setPortalCompatibleCookie } from './cookieIO';
import { normalizePortalProjectList, type PortalProjectOption } from './portalProjectList';
import { ssoJwtCookieName, ssoProjectsInfoCookieName } from './ssoCookieNames';

/** Env-scoped JWT cookie name (was fixed `jwtAuth`). */
export function PORTAL_COOKIE_JWT(): string {
  return ssoJwtCookieName();
}

/** Env-scoped projectsInfo cookie name (was fixed `projectsInfo`). */
export function PORTAL_COOKIE_PROJECTS_INFO(): string {
  return ssoProjectsInfoCookieName();
}

const SHARED_COOKIE_TTL_DAYS = 7;

export interface PortalSelectedProject {
  id?: string;
  name?: string;
  [key: string]: unknown;
}

export interface PortalProjectsInfo {
  customerId?: string;
  customerName?: string;
  userId?: string;
  userName?: string;
  returnUrl?: string;
  projList?: PortalProjectOption[] | Record<string, unknown>[] | (string | number)[];
  selectedProj?: PortalSelectedProject | null;
  propertyList?: unknown[];
  propertySelected?: unknown;
  /** Legacy typo from portal payloads */
  propertySeleted?: unknown;
}

export interface PortalSessionSnapshot {
  authToken: string;
  customerId: string;
  projectId: string;
  projectName: string;
  userId: string;
  userName: string;
  returnUrl: string;
  scopeKey: string;
  projectsInfo: PortalProjectsInfo | null;
}

const STORAGE_KEYS = {
  JWT: 'gantt-planner:jwtAuth',
  CUSTOMER_ID: 'gantt-planner:customerId',
  PROJECT_ID: 'gantt-planner:projectId',
  PROJECT_NAME: 'gantt-planner:projectName',
  USER_ID: 'gantt-planner:userId',
  USER_NAME: 'gantt-planner:userName',
  RETURN_URL: 'gantt-planner:returnUrl',
  SCOPE: 'gantt-planner:sessionScope',
  PROJECTS_INFO: 'gantt-planner:projectsInfo',
} as const;

let memorySnapshot: PortalSessionSnapshot | null = null;

function readLocalStorage(key: string): string | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorage(key: string, value: string): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore quota errors
  }
}

function removeLocalStorage(key: string): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function parseProjectsInfoCookie(raw: string | null): PortalProjectsInfo | null {
  if (!raw?.trim()) {
    return null;
  }
  const candidates = [raw.trim()];
  try {
    candidates.push(decodeURIComponent(raw.trim()));
  } catch {
    // keep single candidate
  }
  for (const candidate of candidates) {
    try {
      const parsed = JSON.parse(candidate) as PortalProjectsInfo;
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch {
      // try next
    }
  }
  return null;
}

function buildScopeKey(customerId: string, projectId: string): string {
  return `${customerId}|${projectId}`;
}

export function buildPortalSessionSnapshot(
  jwt: string,
  info: PortalProjectsInfo,
): PortalSessionSnapshot | null {
  const authToken = String(jwt ?? '').trim();
  const customerId = String(info.customerId ?? '').trim();
  const projectId = String(info.selectedProj?.id ?? '').trim();
  if (!authToken || !customerId || !projectId) {
    return null;
  }
  const projectName = String(info.selectedProj?.name ?? '').trim() || projectId;
  return {
    authToken,
    customerId,
    projectId,
    projectName,
    userId: String(info.userId ?? '').trim(),
    userName: String(info.userName ?? '').trim(),
    returnUrl: String(info.returnUrl ?? '').trim(),
    scopeKey: buildScopeKey(customerId, projectId),
    projectsInfo: info,
  };
}

function persistProjectsInfo(info: PortalProjectsInfo | null): void {
  if (!info) {
    removeLocalStorage(STORAGE_KEYS.PROJECTS_INFO);
    return;
  }
  try {
    writeLocalStorage(STORAGE_KEYS.PROJECTS_INFO, JSON.stringify(info));
  } catch {
    // ignore serialization errors
  }
}

function loadPersistedProjectsInfo(): PortalProjectsInfo | null {
  const raw = readLocalStorage(STORAGE_KEYS.PROJECTS_INFO);
  if (!raw?.trim()) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as PortalProjectsInfo;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function persistSnapshot(snapshot: PortalSessionSnapshot): void {
  writeLocalStorage(STORAGE_KEYS.JWT, snapshot.authToken);
  writeLocalStorage(STORAGE_KEYS.CUSTOMER_ID, snapshot.customerId);
  writeLocalStorage(STORAGE_KEYS.PROJECT_ID, snapshot.projectId);
  writeLocalStorage(STORAGE_KEYS.PROJECT_NAME, snapshot.projectName);
  writeLocalStorage(STORAGE_KEYS.USER_ID, snapshot.userId);
  writeLocalStorage(STORAGE_KEYS.USER_NAME, snapshot.userName);
  writeLocalStorage(STORAGE_KEYS.RETURN_URL, snapshot.returnUrl);
  writeLocalStorage(STORAGE_KEYS.SCOPE, snapshot.scopeKey);
  persistProjectsInfo(snapshot.projectsInfo);
}

export function loadPortalSessionFromStorage(): PortalSessionSnapshot | null {
  const authToken = readLocalStorage(STORAGE_KEYS.JWT)?.trim() ?? '';
  const customerId = readLocalStorage(STORAGE_KEYS.CUSTOMER_ID)?.trim() ?? '';
  const projectId = readLocalStorage(STORAGE_KEYS.PROJECT_ID)?.trim() ?? '';
  if (!authToken || !customerId || !projectId) {
    return null;
  }
  const projectsInfo = loadPersistedProjectsInfo();
  return {
    authToken,
    customerId,
    projectId,
    projectName:
      readLocalStorage(STORAGE_KEYS.PROJECT_NAME)?.trim() || projectId,
    userId: readLocalStorage(STORAGE_KEYS.USER_ID)?.trim() ?? '',
    userName: readLocalStorage(STORAGE_KEYS.USER_NAME)?.trim() ?? '',
    returnUrl: readLocalStorage(STORAGE_KEYS.RETURN_URL)?.trim() ?? '',
    scopeKey: readLocalStorage(STORAGE_KEYS.SCOPE)?.trim() ?? buildScopeKey(customerId, projectId),
    projectsInfo,
  };
}

/** `?projectId=` from portal open (preferred over stale localStorage on handoff). */
export function getHandoffProjectIdFromUrl(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): string {
  try {
    const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
    return String(params.get('projectId') ?? '').trim();
  } catch {
    return '';
  }
}

/**
 * Overlay a portal-selected project onto an existing session snapshot.
 */
export function applyProjectIdOverride(
  snapshot: PortalSessionSnapshot,
  projectId: string,
): PortalSessionSnapshot {
  const nextId = String(projectId ?? '').trim();
  if (!nextId || snapshot.projectId === nextId) {
    return snapshot;
  }

  const fromList = normalizePortalProjectList(snapshot.projectsInfo?.projList).find(
    (p) => p.id === nextId,
  );
  const projectName = fromList?.name?.trim() || nextId;
  const prevSelected = snapshot.projectsInfo?.selectedProj;
  const projectsInfo: PortalProjectsInfo = {
    ...(snapshot.projectsInfo ?? {}),
    customerId: snapshot.customerId,
    selectedProj: {
      ...(prevSelected && typeof prevSelected === 'object' ? prevSelected : {}),
      id: nextId,
      name: projectName,
      ...(fromList?.projectImage ? { project_image: fromList.projectImage } : {}),
    },
    projList: snapshot.projectsInfo?.projList ?? [],
  };

  return {
    ...snapshot,
    projectId: nextId,
    projectName,
    scopeKey: buildScopeKey(snapshot.customerId, nextId),
    projectsInfo,
  };
}

export function readPortalSessionFromCookies(): PortalSessionSnapshot | null {
  let jwt = getCookie(PORTAL_COOKIE_JWT())?.trim() ?? '';
  const infoRaw = getCookie(PORTAL_COOKIE_PROJECTS_INFO());
  if (!infoRaw?.trim()) {
    return null;
  }
  // Portal Auth.setCookieToken may have ingested+erased jwtAuth_* while leaving projectsInfo_*.
  // Prefer cookie JWT; fall back to the last scheduler-persisted token for the same env handoff.
  if (!jwt) {
    jwt = readLocalStorage(STORAGE_KEYS.JWT)?.trim() ?? '';
  }
  if (!jwt) {
    return null;
  }
  const info = parseProjectsInfoCookie(infoRaw);
  if (!info) {
    return null;
  }
  return buildPortalSessionSnapshot(jwt, info);
}

function buildDevFallbackSnapshot(): PortalSessionSnapshot | null {
  const authToken = (import.meta.env.VITE_AUTH_TOKEN as string | undefined)?.trim() ?? '';
  const projectId = (import.meta.env.VITE_PROJECT_ID as string | undefined)?.trim() ?? '';
  const customerId =
    (import.meta.env.VITE_CUSTOMER_ID as string | undefined)?.trim() ||
    (import.meta.env.VITE_HARDCODED_CUSTOMER_ID as string | undefined)?.trim() ||
    'myhome';
  if (!authToken || !projectId) {
    return null;
  }
  const projectName =
    (import.meta.env.VITE_PROJECT_NAME as string | undefined)?.trim() || projectId;
  const projectsInfo: PortalProjectsInfo = {
    customerId,
    selectedProj: { id: projectId, name: projectName },
    projList: [{ id: projectId, name: projectName }],
    userName: (import.meta.env.VITE_USER_NAME as string | undefined)?.trim() ?? '',
  };
  return {
    authToken,
    customerId,
    projectId,
    projectName,
    userId: '',
    userName: projectsInfo.userName ?? '',
    returnUrl: '',
    scopeKey: buildScopeKey(customerId, projectId),
    projectsInfo,
  };
}

export type PortalSessionApplyResult = {
  snapshot: PortalSessionSnapshot | null;
  source: 'portal' | 'storage' | 'env' | 'none';
  scopeChanged: boolean;
  previousScopeKey: string | null;
  previousProjectId: string | null;
};

/**
 * Sync portal cookies into memory + localStorage. Portal cookies win when present.
 * On handoff, `?projectId=` overrides selected project so portal→scheduler stays seamless
 * even if cookie read lags or localStorage still has the previous project.
 */
export function applyPortalSessionFromCookies(): PortalSessionApplyResult {
  const previousScopeKey = readLocalStorage(STORAGE_KEYS.SCOPE);
  const previousProjectId = readLocalStorage(STORAGE_KEYS.PROJECT_ID);
  const urlProjectId = getHandoffProjectIdFromUrl();

  if (!canApplyPortalSession()) {
    memorySnapshot = null;
    return {
      snapshot: null,
      source: 'none',
      scopeChanged: false,
      previousScopeKey,
      previousProjectId,
    };
  }

  const fromPortal = readPortalSessionFromCookies();
  if (fromPortal) {
    const snapshot = urlProjectId
      ? applyProjectIdOverride(fromPortal, urlProjectId)
      : fromPortal;
    const scopeChanged =
      previousScopeKey != null && previousScopeKey !== snapshot.scopeKey;
    persistSnapshot(snapshot);
    memorySnapshot = snapshot;
    return {
      snapshot,
      source: 'portal',
      scopeChanged,
      previousScopeKey,
      previousProjectId,
    };
  }

  const fromStorage = loadPortalSessionFromStorage();
  if (fromStorage) {
    const snapshot =
      urlProjectId && isPortalHandoff()
        ? applyProjectIdOverride(fromStorage, urlProjectId)
        : fromStorage;
    const scopeChanged =
      previousScopeKey != null && previousScopeKey !== snapshot.scopeKey;
    if (snapshot !== fromStorage || scopeChanged) {
      persistSnapshot(snapshot);
    }
    memorySnapshot = snapshot;
    return {
      snapshot,
      source: 'storage',
      scopeChanged,
      previousScopeKey,
      previousProjectId,
    };
  }

  const fromEnv = buildDevFallbackSnapshot();
  if (fromEnv) {
    const snapshot = urlProjectId
      ? applyProjectIdOverride(fromEnv, urlProjectId)
      : fromEnv;
    const scopeChanged =
      previousScopeKey != null && previousScopeKey !== snapshot.scopeKey;
    persistSnapshot(snapshot);
    memorySnapshot = snapshot;
    return {
      snapshot,
      source: 'env',
      scopeChanged,
      previousScopeKey,
      previousProjectId,
    };
  }

  memorySnapshot = null;
  return {
    snapshot: null,
    source: 'none',
    scopeChanged: false,
    previousScopeKey,
    previousProjectId,
  };
}

export function getPortalSessionSnapshot(): PortalSessionSnapshot | null {
  if (!canApplyPortalSession()) {
    return null;
  }
  if (memorySnapshot) {
    return memorySnapshot;
  }
  const fromCookies = readPortalSessionFromCookies();
  if (fromCookies) {
    memorySnapshot = fromCookies;
    return fromCookies;
  }
  const fromStorage = loadPortalSessionFromStorage();
  memorySnapshot = fromStorage;
  return fromStorage;
}

export function clearPersistedPortalSession(): void {
  memorySnapshot = null;
  for (const key of Object.values(STORAGE_KEYS)) {
    removeLocalStorage(key);
  }
}

/**
 * Projects available for in-app switching (from portal `projList` cookie field).
 */
export function getPortalProjectList(): PortalProjectOption[] {
  const snapshot = getPortalSessionSnapshot();
  const fromList = normalizePortalProjectList(snapshot?.projectsInfo?.projList);
  if (fromList.length > 0) {
    return fromList;
  }
  if (snapshot?.projectId) {
    const image = getPortalProjectImage(snapshot.projectId, snapshot);
    return [
      {
        id: snapshot.projectId,
        name: snapshot.projectName || snapshot.projectId,
        ...(image ? { projectImage: image } : {}),
      },
    ];
  }
  return [];
}

/**
 * Image URL for a project from portal `projList` / `selectedProj.project_image`.
 */
export function getPortalProjectImage(
  projectId?: string,
  snapshot = getPortalSessionSnapshot(),
): string {
  const id = String(projectId ?? snapshot?.projectId ?? '').trim();
  if (!id || !snapshot) {
    return '';
  }

  const fromList = normalizePortalProjectList(snapshot.projectsInfo?.projList).find(
    (p) => p.id === id,
  );
  if (fromList?.projectImage) {
    return fromList.projectImage;
  }

  const selected = snapshot.projectsInfo?.selectedProj;
  if (selected && String(selected.id ?? '').trim() === id) {
    const raw = selected as Record<string, unknown>;
    const fromSelected = String(
      raw.project_image ?? raw.projectImage ?? raw.image ?? '',
    ).trim();
    if (fromSelected) {
      return fromSelected;
    }
  }

  return '';
}

export type PortalProjectSwitchResult = {
  success: boolean;
  snapshot: PortalSessionSnapshot | null;
  previousProjectId: string | null;
  scopeChanged: boolean;
};

/**
 * Switch active planning project within the current portal customer scope.
 */
export function switchPortalProject(nextProjectId: string): PortalProjectSwitchResult {
  const targetId = String(nextProjectId ?? '').trim();
  const current = getPortalSessionSnapshot();
  if (!current || !targetId) {
    return {
      success: false,
      snapshot: current,
      previousProjectId: current?.projectId ?? null,
      scopeChanged: false,
    };
  }

  if (targetId === current.projectId) {
    return {
      success: true,
      snapshot: current,
      previousProjectId: current.projectId,
      scopeChanged: false,
    };
  }

  const options = getPortalProjectList();
  const match = options.find((p) => p.id === targetId);
  if (!match) {
    return {
      success: false,
      snapshot: current,
      previousProjectId: current.projectId,
      scopeChanged: false,
    };
  }

  const previousProjectId = current.projectId;
  const baseInfo: PortalProjectsInfo = current.projectsInfo ?? {
    customerId: current.customerId,
    userId: current.userId,
    userName: current.userName,
    returnUrl: current.returnUrl,
    projList: options,
  };

  const updatedInfo: PortalProjectsInfo = {
    ...baseInfo,
    customerId: current.customerId,
    selectedProj: {
      id: match.id,
      name: match.name,
      ...(match.projectImage ? { project_image: match.projectImage } : {}),
    },
    projList: baseInfo.projList?.length ? baseInfo.projList : options,
  };

  const nextSnapshot = buildPortalSessionSnapshot(current.authToken, updatedInfo);
  if (!nextSnapshot) {
    return {
      success: false,
      snapshot: current,
      previousProjectId,
      scopeChanged: false,
    };
  }

  persistSnapshot(nextSnapshot);
  memorySnapshot = nextSnapshot;
  syncSharedSessionCookies(nextSnapshot);

  return {
    success: true,
    snapshot: nextSnapshot,
    previousProjectId,
    scopeChanged: true,
  };
}

/**
 * Rewrite shared `.falconbrick.com` cookies so portal sees the scheduler's
 * active project (and JWT) on the next navigation / deep link.
 */
export function syncSharedSessionCookies(
  snapshot: PortalSessionSnapshot | null = getPortalSessionSnapshot(),
): boolean {
  if (typeof document === 'undefined' || !snapshot?.authToken?.trim()) {
    return false;
  }

  const info: PortalProjectsInfo = {
    ...(snapshot.projectsInfo ?? {}),
    customerId: snapshot.customerId,
    userId: snapshot.userId,
    userName: snapshot.userName,
    returnUrl: snapshot.returnUrl,
    selectedProj: {
      id: snapshot.projectId,
      name: snapshot.projectName,
      ...(snapshot.projectsInfo?.selectedProj
        ? Object.fromEntries(
            Object.entries(snapshot.projectsInfo.selectedProj).filter(
              ([key]) => key !== 'id' && key !== 'name',
            ),
          )
        : {}),
    },
    projList:
      snapshot.projectsInfo?.projList?.length
        ? snapshot.projectsInfo.projList
        : getPortalProjectList(),
  };

  try {
    setPortalCompatibleCookie(PORTAL_COOKIE_JWT(), snapshot.authToken, SHARED_COOKIE_TTL_DAYS);
    setPortalCompatibleCookie(
      PORTAL_COOKIE_PROJECTS_INFO(),
      JSON.stringify(info),
      SHARED_COOKIE_TTL_DAYS,
    );
    return true;
  } catch (err) {
    console.warn('[portalSession] Failed to sync shared session cookies', err);
    return false;
  }
}

export function getPortalReturnUrl(): string {
  return getPortalSessionSnapshot()?.returnUrl?.trim() ?? '';
}

/** Read returnUrl from portal cookie without activating a scheduler session. */
export function peekPortalReturnUrl(): string {
  const infoRaw = getCookie(PORTAL_COOKIE_PROJECTS_INFO());
  const info = parseProjectsInfoCookie(infoRaw);
  return String(info?.returnUrl ?? '').trim();
}

/** True when portal return URL indicates a dev environment (enables in-app project switching). */
export function isPortalDevReturnUrl(): boolean {
  const url = (getPortalReturnUrl() || peekPortalReturnUrl()).toLowerCase();
  return url.includes('dev');
}

export function getPortalUserId(): string {
  return getPortalSessionSnapshot()?.userId?.trim() ?? '';
}

export function getPortalUserName(): string {
  return getPortalSessionSnapshot()?.userName?.trim() ?? '';
}
